import { Link } from "react-router-dom";
import { ShieldCheck, Sparkles, HandshakeIcon } from "lucide-react";

const DISPOSITIVO_MEDICO_IMG =
  "https://customer-assets.emergentagent.com/job_mobility-preview-1/artifacts/ys55pbw4_dispositivo-medico.jpg";

const CHI_SIAMO_IMG =
  "https://customer-assets.emergentagent.com/job_mobility-preview-1/artifacts/ewjzst8c_active.webp";

export default function ChiSiamoSection() {
  return (
    <section
      id="chi-siamo"
      data-testid="chi-siamo-section"
      className="relative py-24 md:py-32 bg-white"
    >
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Image */}
          <div className="lg:col-span-7 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.12)] group">
              <img
                src={CHI_SIAMO_IMG}
                alt="Minicarmobilità - vetrina scooter cabinati e auto usate Zibido San Giacomo"
                className="w-full aspect-[5/4] object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute bottom-5 left-5 right-5 md:right-auto md:max-w-xs glass-panel rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#5ab500] font-bold mb-1">
                  Zibido San Giacomo · Milano Sud
                </div>
                <div className="font-heading text-base md:text-lg font-semibold text-[#1c1f1a] leading-tight">
                  La mobilità urbana che cercavi.
                </div>
              </div>
              {/* Bollino Dispositivo Medico — overlay bottom-right
                  Su mobile (text-box full-width): alzato sopra il riquadro testo, dimensioni ridotte
                  Su desktop (md+): angolo basso-destra (text-box è max-w-xs a sinistra, nessun overlap) */}
              <img
                src={DISPOSITIVO_MEDICO_IMG}
                alt="Dispositivo Medico Classe 1 CE"
                className="absolute bottom-28 right-3 w-14 h-14 sm:bottom-32 sm:right-4 sm:w-16 sm:h-16 md:bottom-5 md:right-5 md:w-24 md:h-24 rounded-full bg-white/95 backdrop-blur-sm ring-2 ring-white shadow-[0_8px_24px_rgba(0,0,0,0.18)] object-contain p-1.5"
              />
            </div>
            {/* Stats panel — new copy */}
            <div className="hidden lg:flex absolute -right-10 top-12 flex-col gap-4 glass-panel rounded-2xl p-5 w-48">
              <Stat n="100%" l="Veicoli ispezionati" />
              <Stat n="12 Mesi" l="Di garanzia inclusa" />
              <Stat n="Pronta" l="Consegna in sede" />
            </div>
          </div>

          {/* Text */}
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#5ab500] font-bold mb-4">
              — Chi Siamo
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#1c1f1a] leading-[1.05]">
              La nostra <span className="italic text-[#5ab500]">visione</span>.
            </h2>

            <p className="mt-6 text-[#4a4f47] leading-relaxed">
              Minicarmobilità nasce a Zibido San Giacomo per dare libertà a
              chi cerca protezione e agilità. Abbiamo unito l'innovazione
              degli scooter cabinati con una selezione chirurgica di auto
              usate certificate. Niente piazzali sterminati o numeri gonfiati,
              solo veicoli di cui ci fidiamo al 100%. Offriamo un Servizio di
              Consegna a domicilio (Info: 329 745 2439) e un Servizio di
              Assistenza tecnica e a domicilio.
            </p>

            {/* Mobile stats (visible on small screens) */}
            <div className="lg:hidden mt-8 grid grid-cols-3 gap-3">
              <MobileStat n="100%" l="Ispezionati" />
              <MobileStat n="12 Mesi" l="Garanzia" />
              <MobileStat n="Pronta" l="Consegna" />
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { icon: ShieldCheck, label: "Garanzia inclusa" },
                { icon: Sparkles, label: "Veicoli selezionati" },
                { icon: HandshakeIcon, label: "Acquisto trasparente" },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="bg-[#f5f6f4] rounded-xl p-4 flex flex-col items-start gap-2 border border-black/[0.04]"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#5ab500]/10 flex items-center justify-center">
                    <Icon className="text-[#5ab500]" size={18} />
                  </div>
                  <div className="text-xs font-semibold text-[#1c1f1a] leading-tight">
                    {label}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-9 flex flex-wrap gap-3">
              <Link
                to="/chi-siamo"
                data-testid="chi-siamo-leggi-tutto"
                className="px-6 py-3 rounded-full bg-[#5ab500] text-white text-sm font-bold hover:bg-[#4a9a00] hover:shadow-[0_8px_20px_rgba(90,181,0,0.4)] transition-all"
              >
                Leggi tutto
              </Link>
              <Link
                to="/contatti"
                data-testid="chi-siamo-contattaci"
                className="px-6 py-3 rounded-full border border-[#1c1f1a]/20 text-[#1c1f1a] text-sm font-semibold hover:border-[#1c1f1a] hover:bg-[#1c1f1a] hover:text-white transition-all"
              >
                Vieni a trovarci
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-heading text-xl md:text-2xl font-bold text-[#5ab500] leading-none">
        {n}
      </div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-[#7a7f77] mt-1 font-semibold">
        {l}
      </div>
    </div>
  );
}

function MobileStat({ n, l }) {
  return (
    <div className="bg-[#f5f6f4] rounded-xl p-3 text-center">
      <div className="font-heading text-lg font-bold text-[#5ab500] leading-tight">
        {n}
      </div>
      <div className="text-[9px] uppercase tracking-[0.1em] text-[#7a7f77] mt-1 font-semibold">
        {l}
      </div>
    </div>
  );
}
